import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Dialog,
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { apiCallVenueInterestedDateWise } from "../../../../Core/Apicall";
import CommonPagination from "../../../Common/CommonPagination/CommonPagination";
import EmptyData from "../../../Common/EmptyData/EmptyData";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import moment from "moment";

interface Venue {
  _id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  profileImagePath: any;
  gender: any;
  relationshipStatus: any;
  interestedDate: any;
  interestedTime: any;
}

const Interested: React.FC<{ venueId: string }> = ({ venueId }) => {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [tableData, setTableData] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMediaForPreview, setSelectedMediaForPreview] = useState<
    any | null
  >(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const handleApiCall = async () => {
    setLoading(true);
    try {
      const data = {
        venueId,
        offset: page,
        limit: rowsPerPage,
        interestedDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined,
      };
      const res = await apiCallVenueInterestedDateWise(data);
      setTableData(res.data || []);
      setTotalItems(res?.totalCount || 0);
    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    setSelectedDate(newDate);
    setPage(1);
  };

  useEffect(() => {
    handleApiCall();
  }, [page, rowsPerPage, selectedDate]);

  const handlePageChange = (value: number) => setPage(value);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: "20px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ width: 200 }}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              format="DD MMM YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          </Box>
        </Box>

        {/* Table Section */}
        <Paper sx={{ borderRadius: 3, overflow: "hidden", position: "relative" }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Profile Image</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Relationship Status</TableCell>
                  <TableCell>Interested Date</TableCell>
                  <TableCell>Interested Time</TableCell>
                </TableRow>
              </TableHead>

              {!loading && (
                <TableBody>
                  {tableData.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={item?.profileImagePath}
                          sx={{
                            width: 65,
                            height: 65,
                            borderRadius: "100px",
                            cursor: "pointer",
                            border: "2px solid #e0e0e0",
                          }}
                          onClick={() =>
                            setSelectedMediaForPreview({
                              type: "Image",
                              url: item?.profileImagePath,
                            })
                          }
                        />
                      </TableCell>

                      <TableCell>{item.fullName || item.firstName + " " + item.lastName}</TableCell>

                      <TableCell>
                        {item.gender}
                      </TableCell>

                      <TableCell>
                        {item.relationshipStatus}
                      </TableCell>

                      <TableCell>
                        {moment(item?.interestedDate, "YYYY-MM-DD").format("DD-MMMM-YYYY")}
                      </TableCell>

                      <TableCell>
                        {moment(item?.interestedTime, "HH:mm").format("hh:mm a")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>

            {loading && (
              <Box sx={{ p: 2 }}>
                <CommonShimmer />
              </Box>
            )}

            {!loading && <EmptyData data={tableData} />}
          </TableContainer>

          <CommonPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(value);
              setPage(1);
            }}
          />
        </Paper>

        {/* Media Preview Dialog */}
        <Dialog
          open={!!selectedMediaForPreview}
          onClose={() => setSelectedMediaForPreview(null)}
        >
          {selectedMediaForPreview?.type === "Image" && (
            <Avatar
              src={selectedMediaForPreview.url}
              variant="rounded"
              sx={{ width: 300, height: "auto" }}
            />
          )}
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Interested;